import { useRef } from 'react';
import type * as React from 'react';

import type { ApiClient } from '../api/client';
import { DEFAULT_TOOL_SCOPES } from '../constants';
import { compareConversationMessages } from '../utils/conversationRunActivity';
import { createConversationAuthorizationEpoch } from '../utils/conversationAuthorizationEpoch';
import type {
  AppGitlabInfo,
  AppItem,
  CodexRun,
  Conversation,
  ConversationAppSecret,
  ConversationAppSecretsResponse,
  ConversationAutopilotSettings,
  ConversationGitlabInfo,
  ConversationMessage,
  ModelConfig,
  OpsOverview,
  OpsSecret,
  QueueItem,
  RunEvent,
  ToolTokenStatus
} from '../types';

type Args = {
  apiClient: ApiClient;
  appId: string;
  conversationId: string;
  conversation: Conversation | null;
  models: ModelConfig[];
  loadingMessages: boolean;
  loadingOlderMessages: boolean;
  conversationSecretAppId: string;
  /** Changes whenever server-derived conversation authorization changes. */
  authorizationEpoch: string;
  canLoadToolTokenStatus: boolean;
  canLoadGitlab: boolean;
  canLoadAutopilot: boolean;
  canLoadModels: boolean;
  canLoadRuntime: boolean;
  setError: (message: string | null) => void;
  reportErrorLocal: (
    context: string,
    error: unknown,
    setError: (message: string | null) => void,
    setLocalError: (message: string | null) => void
  ) => void;
  setConversation: React.Dispatch<React.SetStateAction<Conversation | null>>;
  setMessages: React.Dispatch<React.SetStateAction<ConversationMessage[]>>;
  setHasMoreMessages: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadingMessages: React.Dispatch<React.SetStateAction<boolean>>;
  setInitialMessagesLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadingOlderMessages: React.Dispatch<React.SetStateAction<boolean>>;
  setQueue: React.Dispatch<React.SetStateAction<QueueItem[]>>;
  setRuns: React.Dispatch<React.SetStateAction<CodexRun[]>>;
  setRunEvents: React.Dispatch<React.SetStateAction<RunEvent[]>>;
  setConversationError: React.Dispatch<React.SetStateAction<string | null>>;
  setGitlabInfo: React.Dispatch<React.SetStateAction<ConversationGitlabInfo | null>>;
  setMultiGitlabApps: React.Dispatch<React.SetStateAction<AppGitlabInfo[]>>;
  setGitlabError: React.Dispatch<React.SetStateAction<string | null>>;
  setAutopilotMode: React.Dispatch<React.SetStateAction<'off' | 'semi' | 'full'>>;
  setModels: React.Dispatch<React.SetStateAction<ModelConfig[]>>;
  setApps: React.Dispatch<React.SetStateAction<AppItem[]>>;
  setToolTokenStatus: React.Dispatch<React.SetStateAction<ToolTokenStatus | null>>;
  setToolTokenValue: React.Dispatch<React.SetStateAction<string | null>>;
  setToolTokenScopes: React.Dispatch<React.SetStateAction<string[]>>;
  setConversationSecrets: React.Dispatch<React.SetStateAction<ConversationAppSecret[]>>;
  setConversationOpsSecrets: React.Dispatch<React.SetStateAction<OpsSecret[]>>;
  setConversationSecretsBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setConversationSecretsError: React.Dispatch<React.SetStateAction<string | null>>;
  setConversationSecretDraftKey: React.Dispatch<React.SetStateAction<string>>;
};

function mergeCurrentRowsById<T extends { id: string }>(fetched: T[], current: T[]) {
  const merged = new Map(fetched.map((row) => [row.id, row]));
  for (const row of current) {
    merged.set(row.id, row);
  }
  return Array.from(merged.values());
}

export function useConversationPageLoaders({
  apiClient,
  appId,
  conversationId,
  conversation,
  models,
  loadingMessages,
  loadingOlderMessages,
  conversationSecretAppId,
  authorizationEpoch,
  canLoadToolTokenStatus,
  canLoadGitlab,
  canLoadAutopilot,
  canLoadModels,
  canLoadRuntime,
  setError,
  reportErrorLocal,
  setConversation,
  setMessages,
  setHasMoreMessages,
  setLoadingMessages,
  setInitialMessagesLoaded,
  setLoadingOlderMessages,
  setQueue,
  setRuns,
  setRunEvents,
  setConversationError,
  setGitlabInfo,
  setMultiGitlabApps,
  setGitlabError,
  setAutopilotMode,
  setModels,
  setApps,
  setToolTokenStatus,
  setToolTokenValue,
  setToolTokenScopes,
  setConversationSecrets,
  setConversationOpsSecrets,
  setConversationSecretsBusy,
  setConversationSecretsError,
  setConversationSecretDraftKey
}: Args) {
  const oldestMessageIdRef = useRef<string | null>(null);
  const currentConversationIdRef = useRef(conversationId);
  const conversationStateVersionRef = useRef(0);
  const liveStateRevisionRef = useRef(0);
  const loadMessagesRequestIdRef = useRef(0);
  const runCursorRef = useRef<number | null>(null);
  const loadQueueInFlightRef = useRef(false);
  const loadRunsInFlightRef = useRef(false);
  const authorizationEpochGuardRef = useRef(createConversationAuthorizationEpoch(authorizationEpoch));

  // This assignment intentionally happens during render. Effects run after a
  // new access summary is committed, which is too late to stop an already
  // resolved privileged request from restoring data cleared during demotion.
  // Bumping the existing request generation invalidates every in-flight
  // loader before the narrower capability controls can render.
  if (authorizationEpochGuardRef.current.update(authorizationEpoch)) {
    conversationStateVersionRef.current += 1;
    loadMessagesRequestIdRef.current += 1;
  }

  currentConversationIdRef.current = conversationId;

  const captureConversationStateVersion = () => conversationStateVersionRef.current;
  const isCurrentConversationRequest = (requestConversationId: string, requestVersion: number) =>
    currentConversationIdRef.current === requestConversationId &&
    conversationStateVersionRef.current === requestVersion;

  async function loadConversation() {
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    try {
      const data = await apiClient.get<Conversation>(`/api/conversations/${requestConversationId}`);
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setConversation(data);
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load conversation', err, setError, setConversationError);
    }
  }

  async function loadConversationAutopilot() {
    if (!canLoadAutopilot) {
      setAutopilotMode('semi');
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    try {
      const data = await apiClient.get<ConversationAutopilotSettings>(
        `/api/conversations/${requestConversationId}/autopilot`
      );
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setAutopilotMode(data.mode);
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load autopilot settings', err, setError, setConversationError);
    }
  }

  async function loadModels() {
    if (!canLoadModels) {
      setModels([]);
      setApps([]);
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    try {
      const [modelsData, appsData] = await Promise.all([
        apiClient.get<ModelConfig[]>('/api/me/models'),
        apiClient.get<AppItem[]>('/api/apps')
      ]);
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setModels(modelsData);
      setApps(appsData);
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load models', err, setError, setConversationError);
    }
  }

  async function loadGitlabInfo() {
    if (!canLoadGitlab) {
      setGitlabInfo(null);
      setGitlabError(null);
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    try {
      const data = await apiClient.get<{ gitlab: ConversationGitlabInfo | null }>(
        `/api/conversations/${requestConversationId}/gitlab`
      );
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setGitlabInfo(data.gitlab);
      setGitlabError(data.gitlab?.error ?? null);
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load GitLab status', err, setError, setConversationError);
    }
  }

  async function loadMultiGitlabInfo() {
    if (!canLoadGitlab) {
      setMultiGitlabApps([]);
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    try {
      const data = await apiClient.get<{ apps: AppGitlabInfo[] }>(
        `/api/conversations/${requestConversationId}/gitlab/multi`
      );
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setMultiGitlabApps(data.apps);
      // Set primary gitlab info from the first app (the main app)
      if (data.apps.length > 0) {
        setGitlabInfo(data.apps[0].gitlab);
        setGitlabError(data.apps[0].gitlab?.error ?? null);
      }
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load multi-app GitLab status', err, setError, setConversationError);
    }
  }

  async function loadMessages(force = false) {
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    const requestLiveStateRevision = liveStateRevisionRef.current;
    if (!requestConversationId) {
      return;
    }
    if (loadingMessages && !force) {
      return;
    }
    const requestId = loadMessagesRequestIdRef.current + 1;
    loadMessagesRequestIdRef.current = requestId;
    setLoadingMessages(true);
    try {
      const data = await apiClient.get<ConversationMessage[]>(
        `/api/conversations/${requestConversationId}/messages?limit=50`
      );
      if (
        loadMessagesRequestIdRef.current !== requestId ||
        !isCurrentConversationRequest(requestConversationId, requestVersion)
      ) {
        return;
      }
      const sorted = [...data].reverse();
      if (liveStateRevisionRef.current === requestLiveStateRevision) {
        setMessages(sorted);
      } else {
        setMessages((current) =>
          mergeCurrentRowsById(sorted, current).sort(compareConversationMessages)
        );
      }
      setHasMoreMessages(data.length === 50);
      if (sorted.length > 0) {
        oldestMessageIdRef.current = sorted[0].id;
      }
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load messages', err, setError, setConversationError);
    } finally {
      if (
        loadMessagesRequestIdRef.current === requestId &&
        isCurrentConversationRequest(requestConversationId, requestVersion)
      ) {
        setInitialMessagesLoaded(true);
        setLoadingMessages(false);
      }
    }
  }

  async function loadOlderMessages() {
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId || loadingOlderMessages || !oldestMessageIdRef.current) {
      return;
    }
    setLoadingOlderMessages(true);
    try {
      const data = await apiClient.get<ConversationMessage[]>(
        `/api/conversations/${requestConversationId}/messages?before=${oldestMessageIdRef.current}&limit=50`
      );
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      if (data.length > 0) {
        const sorted = [...data].reverse();
        setMessages((prev) => [...sorted, ...prev]);
        oldestMessageIdRef.current = sorted[0].id;
      }
      setHasMoreMessages(data.length === 50);
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load older messages', err, setError, setConversationError);
    } finally {
      if (isCurrentConversationRequest(requestConversationId, requestVersion)) {
        setLoadingOlderMessages(false);
      }
    }
  }

  async function loadQueue() {
    if (!canLoadRuntime) {
      setQueue([]);
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    const requestLiveStateRevision = liveStateRevisionRef.current;
    if (!requestConversationId || loadQueueInFlightRef.current) {
      return;
    }
    loadQueueInFlightRef.current = true;
    try {
      const data = await apiClient.get<QueueItem[]>(`/api/conversations/${requestConversationId}/queue`);
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      if (liveStateRevisionRef.current === requestLiveStateRevision) {
        setQueue(data);
      } else {
        setQueue((current) => mergeCurrentRowsById(data, current));
      }
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load queue', err, setError, setConversationError);
    } finally {
      loadQueueInFlightRef.current = false;
    }
  }

  async function loadRuns() {
    if (!canLoadRuntime) {
      setRuns([]);
      setRunEvents([]);
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    const requestLiveStateRevision = liveStateRevisionRef.current;
    if (!requestConversationId || loadRunsInFlightRef.current) {
      return;
    }
    loadRunsInFlightRef.current = true;
    try {
      const data = await apiClient.get<CodexRun[]>(`/api/conversations/${requestConversationId}/runs`);
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      if (liveStateRevisionRef.current === requestLiveStateRevision) {
        setRuns(data);
      } else {
        setRuns((current) => mergeCurrentRowsById(data, current));
      }
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load runs', err, setError, setConversationError);
    } finally {
      loadRunsInFlightRef.current = false;
    }
  }

  async function loadToolTokenStatus() {
    if (!canLoadToolTokenStatus) {
      setToolTokenStatus(null);
      setToolTokenValue(null);
      setToolTokenScopes(DEFAULT_TOOL_SCOPES);
      return;
    }
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    if (!requestConversationId) {
      return;
    }
    try {
      const data = await apiClient.get<ToolTokenStatus>(
        `/api/conversations/${requestConversationId}/tool-token`
      );
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setToolTokenStatus(data);
      if (data.scopes?.length) {
        setToolTokenScopes(data.scopes);
      }
      if (!data.active) {
        setToolTokenValue(null);
      }
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load tool token', err, setError, setConversationError);
    }
  }

  async function loadConversationSecrets() {
    const requestConversationId = conversationId;
    const requestVersion = captureConversationStateVersion();
    const requestAppId = conversationSecretAppId;
    if (!requestConversationId || !requestAppId) {
      return;
    }
    setConversationSecretsBusy(true);
    setConversationSecretsError(null);
    try {
      const [ops, overrides] = await Promise.all([
        apiClient.get<OpsOverview>(`/api/apps/${requestAppId}/ops`),
        apiClient.get<ConversationAppSecretsResponse>(
          `/api/conversations/${requestConversationId}/secrets?app_id=${encodeURIComponent(
            requestAppId
          )}`
        )
      ]);
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      setConversationOpsSecrets(ops.secrets ?? []);
      setConversationSecrets(overrides.secrets ?? []);
      setConversationSecretDraftKey((prev) => {
        if (prev && (ops.secrets ?? []).some((secret) => secret.key === prev)) {
          return prev;
        }
        return ops.secrets?.[0]?.key ?? '';
      });
    } catch (err) {
      if (!isCurrentConversationRequest(requestConversationId, requestVersion)) {
        return;
      }
      reportErrorLocal('Failed to load conversation secrets', err, setError, setConversationError);
      setConversationSecretsError(err instanceof Error ? err.message : String(err));
    } finally {
      if (isCurrentConversationRequest(requestConversationId, requestVersion)) {
        setConversationSecretsBusy(false);
      }
    }
  }

  async function loadAll() {
    await Promise.all([
      loadConversation(),
      loadMessages(true),
      loadQueue(),
      loadRuns(),
      loadToolTokenStatus(),
      loadGitlabInfo(),
      loadMultiGitlabInfo(),
      loadConversationAutopilot(),
      loadModels()
    ]);
  }

  function resetConversationDerivedState() {
    conversationStateVersionRef.current += 1;
    liveStateRevisionRef.current += 1;
    loadMessagesRequestIdRef.current += 1;
    oldestMessageIdRef.current = null;
    runCursorRef.current = null;
    setConversation(null);
    setMessages([]);
    setInitialMessagesLoaded(false);
    setHasMoreMessages(false);
    setLoadingMessages(false);
    setLoadingOlderMessages(false);
    setQueue([]);
    setRuns([]);
    setRunEvents([]);
    setConversationError(null);
    setGitlabInfo(null);
    setMultiGitlabApps([]);
    setGitlabError(null);
    setAutopilotMode('semi');
    setToolTokenValue(null);
    setToolTokenStatus(null);
    setToolTokenScopes(DEFAULT_TOOL_SCOPES);
    setConversationSecrets([]);
    setConversationOpsSecrets([]);
    setConversationSecretsError(null);
    setConversationSecretDraftKey('');
  }

  return {
    oldestMessageIdRef,
    runCursorRef,
    currentConversationIdRef,
    conversationStateVersionRef,
    liveStateRevisionRef,
    captureConversationStateVersion,
    isCurrentConversationRequest,
    loadConversation,
    loadConversationAutopilot,
    loadModels,
    loadGitlabInfo,
    loadMultiGitlabInfo,
    loadMessages,
    loadOlderMessages,
    loadQueue,
    loadRuns,
    loadToolTokenStatus,
    loadConversationSecrets,
    loadAll,
    resetConversationDerivedState
  };
}
